import type { ReactElement } from 'react';

import {
  KestrixCheckCircleIcon,
  KestrixErrorCircleIcon,
} from '../KestrixSvgIcons.jsx';
import { colorTokens } from '../tokens/colors.ts';
import { HelperMessageRow } from './styles.ts';
import type { HelperMessageVariant, KestrixHelperMessageProps } from './types.ts';

const HELPER_ICON_SIZE = 12;

const helperColors = {
  success: colorTokens.semantic.status.successHighEmphasis.border,
  error: colorTokens.semantic.status.error,
  info: colorTokens.text.subtextNeutral,
} as const;

const helperIconProps = {
  size: HELPER_ICON_SIZE,
  'aria-hidden': true,
  'data-testid': 'kestrix-helper-icon',
} as const;

const HelperIcon = ({ variant }: { variant: HelperMessageVariant }): ReactElement | null => {
  if (variant === 'success') {
    return <KestrixCheckCircleIcon {...helperIconProps} color={helperColors.success} />;
  }
  if (variant === 'error') {
    return <KestrixErrorCircleIcon {...helperIconProps} color={helperColors.error} />;
  }
  return null;
};

/**
 * Reusable helper / validation message for form fields.
 *
 * Default (`info`) is text only. Error and success include a 12px status icon.
 *
 * @example
 * <KestrixHelperMessage message="Must be at least 8 characters" variant="error" />
 */
export const KestrixHelperMessage = ({
  message,
  variant = 'info',
  id,
}: KestrixHelperMessageProps) => (
  <HelperMessageRow
    id={id}
    data-testid="kestrix-helper-message"
    aria-live="polite"
    style={{ color: helperColors[variant] }}
  >
    <HelperIcon variant={variant} />
    {message}
  </HelperMessageRow>
);
